import React, { useState, useRef } from 'react';
import { X, Upload, ChevronDown } from 'lucide-react';
import '../../../styles/pages/alerts.css';
import '../../../styles/pages/help-support.css';

interface RaiseTicketModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (_ref: string, _subject: string, _priority: string, _description: string) => void;
}

const SUBJECTS = [
  'Device not connecting',
  'Dashboard not loading',
  'Login Issue',
  'Password Reset Error',
  'API Response Delay',
  'Notification Bug',
  'Other',
];

const SUBJECT_PRIORITY: Record<string, string> = {
  'Device not connecting': 'High',
  'Dashboard not loading': 'High',
  'Login Issue':           'High',
  'API Response Delay':    'High',
  'Password Reset Error':  'Medium',
  'Notification Bug':      'Medium',
  'Other':                 'Low',
};

const RaiseTicketModal: React.FC<RaiseTicketModalProps> = ({ isOpen, onClose, onSubmit }) => {
  const [subject, setSubject] = useState('');
  const [showSubjectDropdown, setShowSubjectDropdown] = useState(false);
  const [device, setDevice] = useState('');
  const [message, setMessage] = useState('');
  const [fileName, setFileName] = useState('');
  const [errors, setErrors] = useState<{ subject?: string; message?: string }>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const subjectBtnRef = useRef<HTMLButtonElement>(null);

  if (!isOpen) return null;

  const validate = () => {
    const e: typeof errors = {};
    if (!subject) e.subject = 'Please select a subject.';
    if (!message.trim()) e.message = 'Message is required.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const generateRef = () =>
    `TKT-${Math.floor(100000 + Math.random() * 900000)}-${Math.floor(100 + Math.random() * 900)}`;

  const handleSubmit = () => {
    if (!validate()) return;
    const priority = SUBJECT_PRIORITY[subject] || 'Low';
    onSubmit(generateRef(), subject, priority, message);
    handleClose();
  };

  const handleClose = () => {
    setSubject('');
    setDevice('');
    setMessage('');
    setFileName('');
    setErrors({});
    setShowSubjectDropdown(false);
    onClose();
  };

  const handleFileDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) setFileName(file.name);
  };

  return (
    <div className="modal-overlay zoomOutLevel" onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>

        <div className="modal-header">
          <div>
            <p className="na-section-label" style={{ margin: 0 }}>SUBMIT A SUPPORT TICKET</p>
          </div>
          <button className="modal-close" onClick={handleClose}><X size={20} /></button>
        </div>

        <div className="modal-form">

          {/* Subject */}
          <div className="form-group">
            <label className="form-label">Subject</label>
            <div style={{ position: 'relative' }}>
              <button
                ref={subjectBtnRef}
                type="button"
                className={`form-dropdown-button ${showSubjectDropdown ? 'active' : ''}`}
                onClick={() => setShowSubjectDropdown((p) => !p)}
              >
                <span style={{ color: subject ? 'var(--color-text)' : undefined }}>
                  {subject || 'Select Subject'}
                </span>
                <ChevronDown size={16} />
              </button>
              {showSubjectDropdown && (
                <div className="dropdown-menu" style={{ zIndex: 9999 }}>
                  {SUBJECTS.map((s) => (
                    <div
                      key={s}
                      className="dropdown-item"
                      onClick={() => { setSubject(s); setShowSubjectDropdown(false); }}
                    >
                      {s}
                    </div>
                  ))}
                </div>
              )}
            </div>
            {errors.subject && <p style={{ color: '#E05252', fontSize: '0.75rem', marginTop: 4 }}>{errors.subject}</p>}
          </div>

          {/* Device / Site */}
          <div className="form-group">
            <label className="form-label">Device / Site <span style={{ color: 'var(--color-sub-title-text)', fontWeight: 400 }}>(optional)</span></label>
            <input
              type="text"
              className="form-input"
              placeholder="e.g. Air Sense Pro X1 – Building A"
              value={device}
              onChange={(e) => setDevice(e.target.value)}
            />
          </div>

          {/* Message */}
          <div className="form-group">
            <label className="form-label">Message</label>
            <textarea
              className="form-input"
              placeholder="Describe your issue in detail.."
              rows={4}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              style={{ resize: 'vertical' }}
            />
            {errors.message && <p style={{ color: '#E05252', fontSize: '0.75rem', marginTop: 4 }}>{errors.message}</p>}
          </div>

          {/* File Upload */}
          <div
            className="hs-file-drop"
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleFileDrop}
          >
            <div className="hs-file-drop-icon"><Upload size={28} /></div>
            {fileName ? (
              <p style={{ color: 'var(--color-text)', fontWeight: 500 }}>{fileName}</p>
            ) : (
              <>
                <p>Drop your file here, or <span className="hs-browse">click to browse</span></p>
                <p>Supports CSV and Excel files</p>
                <p>(max 10MB)</p>
              </>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls"
              style={{ display: 'none' }}
              onChange={(e) => { if (e.target.files?.[0]) setFileName(e.target.files[0].name); }}
            />
          </div>

        </div>

        <div className="modal-actions">
          <button className="alerts-add-button w-full" onClick={handleSubmit}>Submit Ticket</button>
        </div>

      </div>
    </div>
  );
};

export default RaiseTicketModal;
